import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import LanIcon from '@mui/icons-material/Lan';
import { services } from '../../data/services';

const Services = () => {

  return (
  <Box component="section" id="services" sx={{ py: { xs: 12, md: 16 }, backgroundColor: 'background.default' }}>
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Chip 
          label="Our Services" 
          color="secondary" 
          size="small" 
          sx={{ mb: 2, fontWeight: 600 }}
        />
        <Typography variant="h2" sx={{ 
          mb: 3, 
          color: 'primary.main',
          fontWeight: 700,
          fontSize: { xs: '2rem', md: '3rem' },
          letterSpacing: '-0.02em'
        }}>
          Comprehensive Mechanical Solutions
        </Typography>
        <Typography variant="body1" sx={{ 
          maxWidth: 800, 
          mx: 'auto',
          fontSize: '1.125rem',
          color: 'text.secondary',
          lineHeight: 1.7
        }}>
          From HVAC design-build to specialized equipment and complex system integrations, ANR owns every phase of the
          mechanical lifecycle with precision planning, prefabrication, and field execution. We deliver excellence through
          innovation, expertise, and unwavering commitment to quality.
        </Typography>
      </Box>
      
      <Grid container spacing={4}>
        {services.map((service, index) => {
          const iconMap = {
            DesignServices: DesignServicesIcon,
            Plumbing: PlumbingIcon,
            PrecisionManufacturing: PrecisionManufacturingIcon,
            SettingsEthernet: SettingsEthernetIcon,
            Lan: LanIcon,
          };
          const IconComponent = iconMap[service.icon] || DesignServicesIcon;
          return (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={service.title}>
              <Fade in={true} timeout={600 + (index * 100)}>
                <Card sx={{ 
                  height: '100%', 
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', 
                  '&:hover': { 
                    transform: 'translateY(-8px)',
                    boxShadow: (theme) => theme.shadows[16],
                    '& .service-icon': {
                      transform: 'scale(1.1) rotate(5deg)',
                    }
                  },
                  borderRadius: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  background: (t) => {
                    const palettes = [
                      [t.palette.primary.light, t.palette.primary.main],
                      [t.palette.secondary.light, t.palette.secondary.main],
                      [t.palette.info.light, t.palette.info.main],
                      [t.palette.success.light, t.palette.success.main],
                      [t.palette.warning.light, t.palette.warning.main],
                    ];
                    const pair = palettes[index % palettes.length];
                    return `linear-gradient(135deg, ${pair[0]}15 0%, ${pair[1]}10 100%)`;
                  }
                }}>
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Box sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '24px',
                      background: (t) => {
                        const palettes = [
                          [t.palette.primary.main, t.palette.primary.dark],
                          [t.palette.secondary.main, t.palette.secondary.dark],
                          [t.palette.info.main, t.palette.info.dark],
                          [t.palette.success.main, t.palette.success.dark],
                          [t.palette.warning.main, t.palette.warning.dark],
                        ];
                        const pair = palettes[index % palettes.length];
                        return `linear-gradient(135deg, ${pair[0]} 0%, ${pair[1]} 100%)`;
                      },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      mb: 3,
                      transition: 'all 0.3s ease',
                      '&.service-icon': {
                        transition: 'transform 0.3s ease'
                      }
                    }} className="service-icon">
                      <IconComponent sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h5" sx={{ 
                      mb: 2,
                      fontWeight: 600,
                      color: 'primary.main',
                      fontSize: '1.375rem'
                    }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary',
                      lineHeight: 1.7,
                      fontSize: '1rem'
                    }}>
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          );
        })}
      </Grid>
      
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Typography variant="body2" sx={{ 
          color: 'text.secondary',
          mb: 2,
          fontWeight: 500
        }}>
          Need a custom solution?
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          href="tel:(516) 414-0165"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            fontSize: '1.125rem',
            boxShadow: '0 8px 25px rgba(255, 122, 24, 0.3)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(255, 122, 24, 0.4)'
            }
          }}
        >
          Get a Custom Quote
        </Button>
      </Box>
    </Container>
  </Box>
  );
};

export default Services;
